import { LitElement, TemplateResult, html } from 'lit';
import { customElement, property } from 'lit/decorators.js';
// se-main-nav.global.pcss imported through src/styles/global.pcss
import { SeMainHeader } from '../se-main-header/se-main-header';
import { MobileControllerLegacy } from '../../controllers/mobile-controller-legacy';
import { whenParsed } from '../../utilities/when-parsed';
import '../se-dropdown/se-dropdown';

/**
 * The SeMainNav component
 * @element se-main-nav
 */
@customElement('se-main-nav')
export class SeMainNav extends LitElement {
  mobileControllerLegacy = new MobileControllerLegacy(this);

  /**
   * Exposes full component to light-DOM
   */
  protected createRenderRoot() {
    return this;
  }

  @property({ type: Object })
  nav: HTMLElement;

  @property({ type: Object })
  mainMenu: HTMLElement;

  @property({ type: Array })
  topLevelLinks: Array<HTMLElement> = [];

  @property({ type: Array })
  topLevelItems: Array<HTMLElement> = [];

  @property({ type: Array })
  topLevelLists: Array<HTMLElement> = [];

  @property({ type: Array })
  topLevelDropdowns: Array<HTMLElement> = [];

  @property({ type: Array })
  subLevelLinks: Array<HTMLElement> = [];

  @property({ type: Boolean })
  isMobile: boolean;

  seed = Math.floor(Math.random() * 1000000).toString();

  render(): TemplateResult {
    return html``;
  }

  async firstUpdated() {
    // This component reads its own light-DOM children. When it is registered
    // by the critical bundle it upgrades mid-parse, before those children
    // exist, so wait for them. See utilities/when-parsed.ts (SE-1004).
    await whenParsed();

    try {
      // Every step below reads the slotted <nav> markup or the collections
      // built from it. If that markup is absent there is nothing to enhance,
      // and continuing would dereference null. See setTopLevelPropsIDs().
      if (!this.setTopLevelPropsIDs()) {
        return;
      }

      this.setClassNames();
      this.setIsMobile();
      this.handleButtons();
      this.addEventListeners();
      this.handleDropdownOnLoad();
      window.addEventListener('resize', () => this.setDropdownWidth);
    } finally {
      // Reveal the menu (see se-main-nav.global.pcss), even if setup bailed.
      this.setAttribute('ready', '');
    }
  }

  updated() {
    this.setDropdownWidth();
  }

  /**
   * Collects the slotted nav markup and assigns ids to its top-level items.
   *
   * @returns True when the expected markup was found and the collections are
   *   populated; false when there is nothing to enhance, in which case callers
   *   must not run the remaining setup steps.
   */
  setTopLevelPropsIDs(): boolean {
    this.nav = this.querySelector('nav') as HTMLElement;
    if (!this.nav) {
      // No nav markup was slotted in; nothing to enhance.
      return false;
    }
    this.mainMenu = [...this.nav.children].filter(
      child => child.localName === 'ul'
    )[0] as HTMLElement;

    if (!this.mainMenu) {
      // A <nav> without its menu list; the rest of setup has nothing to act on.
      return false;
    }

    [...this.mainMenu.children]
      .filter(child => child.localName === 'li')
      .forEach((li, i) => {
        li.setAttribute('id', `item--${this.seed}-${i}`);
        this.topLevelItems.push(li as HTMLElement);
        const mainElements = [...li.children] as HTMLElement[];

        mainElements.forEach(el => {
          if (el.localName === 'button') {
            el.setAttribute('id', `button--${this.seed}-${i}`);
            this.topLevelDropdowns.push(el);
          }
          if (el.localName === 'ul') {
            el.setAttribute('id', `sub--${this.seed}-${i}`);
            this.topLevelLists.push(el);
          }
          if (el.localName === 'a') {
            el.setAttribute('id', `link--${this.seed}-${i}`);
            this.topLevelLinks.push(el);
          }
        });
      });

    return true;
  }

  setClassNames() {
    this.nav.classList.add('se-main-nav--nav');
    this.mainMenu.classList.add('se-main-nav--menu');
    this.topLevelLinks.forEach(a => a.classList.add('se-main-nav--link'));
    this.topLevelItems.forEach(li => li.classList.add('se-main-nav--item'));
    this.topLevelDropdowns.forEach(button =>
      button.classList.add('se-main-nav--button')
    );
    this.topLevelLists.forEach(ul => ul.classList.add('se-main-nav--sub-menu'));

    this.topLevelLists.forEach(ul => {
      const list = ul as HTMLUListElement;
      [...list.children].forEach(li => {
        const subLink = li.children[0] as HTMLElement;
        subLink.classList.add('se-main-nav--sub-link');
        this.subLevelLinks.push(subLink);
      });
    });
  }

  addEventListeners() {
    const parentHeader = document.querySelector(
      'se-main-header'
    ) as SeMainHeader;
    parentHeader.addEventListener('keydown', parentHeader.handleEscape);

    this.topLevelDropdowns.forEach(button =>
      button.addEventListener('mouseover', parentHeader.applyOverlay)
    );
    this.topLevelDropdowns.forEach(button =>
      button.addEventListener('mouseout', parentHeader.removeOverlay)
    );
    this.topLevelDropdowns.forEach(button =>
      button.addEventListener('blur', parentHeader.removeOverlay)
    );
    this.topLevelDropdowns.forEach(button =>
      button.addEventListener('keydown', parentHeader.handleOverlayOnKeydown)
    );
  }

  handleButtons() {
    this.topLevelDropdowns.forEach(button => {
      const el = button as HTMLElement;
      const listID = 'sub--'.concat(el.id.split('--')[1]);
      const replacement = document.createElement('se-dropdown');
      this.topLevelDropdowns.push(replacement);

      const list = this.topLevelLists.filter(el => el.id === listID)[0];
      list.setAttribute('slot', 'dropdown');
      replacement.appendChild(list);
      replacement.setAttribute('trigger-text', el.title);
      replacement.setAttribute('trigger-label', `${el.title} menu`);
      replacement.setAttribute('id', el.id);

      // Listen for dropdown open events to recalculate width dynamically
      replacement.addEventListener('mouseenter', () => {
        // Use requestAnimationFrame to ensure dropdown is rendered before calculating
        requestAnimationFrame(() => {
          this.calculateDropdownWidth(replacement);
        });
      });

      el.insertAdjacentElement('beforebegin', replacement);
      el.remove();
    });

    //cleanup
    this.topLevelDropdowns = this.topLevelDropdowns.filter(
      el => el.localName === 'se-dropdown'
    );
  }

  /**
   * Calculate width for a single dropdown element when it opens.
   * This ensures we always use the current dimensions, including loaded backgrounds.
   * @param el - The dropdown element
   */
  private calculateDropdownWidth(el: HTMLElement): void {
    if (!el) return;

    const header = document.querySelector('se-main-header') as HTMLElement;
    if (!header) return;

    const menu = document.querySelector('.se-main-nav--menu') as HTMLElement;
    if (!menu) return;

    if (!header.shadowRoot) return;

    const searchElement = header.shadowRoot.querySelector(
      '.se-main-header--search'
    ) as HTMLElement;
    if (!searchElement) return;

    if (!el.shadowRoot) return;

    const dropdownPanel = el.shadowRoot.querySelector(
      '.dropdown__panel'
    ) as HTMLElement;
    if (!dropdownPanel) return;

    const dropdownSlotWrapper = el.shadowRoot.querySelector(
      '.control-spacer'
    ) as HTMLElement;

    let elRect, searchRect, headerRect, menuRect;
    try {
      elRect = el.getBoundingClientRect();
      searchRect = searchElement.getBoundingClientRect();
      headerRect = header.getBoundingClientRect();
      menuRect = menu.getBoundingClientRect();
    } catch (error) {
      return;
    }

    const panelPostionRight = elRect.right - searchRect.right;
    const panelPostionTop = elRect.height;
    const searchElementPositionTop = headerRect.height - menuRect.top;

    try {
      if (this.mobileControllerLegacy.isMobile) {
        dropdownPanel.style.right = `0px`;
        if (dropdownSlotWrapper) {
          dropdownSlotWrapper.style.top = `0px`;
        }
      } else {
        dropdownPanel.style.right = `${panelPostionRight}px`;
        dropdownPanel.style.top = `${panelPostionTop}px`;
        if (dropdownSlotWrapper) {
          dropdownSlotWrapper.style.top = `${searchElementPositionTop - panelPostionTop}px`;
        }
      }
    } catch (error) {
      return;
    }
  }

  /**
   * Positions dropdown panel on desktop.
   * Called on component updates and window resize events.
   */
  setDropdownWidth() {
    const header = document.querySelector('se-main-header') as HTMLElement;
    if (!header) return;

    const menu = document.querySelector('.se-main-nav--menu') as HTMLElement;
    if (!menu) return;

    if (!header.shadowRoot) return;

    const searchElement = header.shadowRoot.querySelector(
      '.se-main-header--search'
    ) as HTMLElement;
    if (!searchElement) return;

    const thisComponent = document.querySelector('se-main-nav') as SeMainNav;
    if (!thisComponent) return;

    if (!thisComponent.topLevelDropdowns || !Array.isArray(thisComponent.topLevelDropdowns)) {
      return;
    }

    thisComponent.topLevelDropdowns.forEach(el => {
      if (!el) return;
      if (!el.shadowRoot) return;

      const dropdownPanel = el.shadowRoot.querySelector(
        '.dropdown__panel'
      ) as HTMLElement;

      const dropdownSlotWrapper = el.shadowRoot.querySelector(
        '.control-spacer'
      ) as HTMLElement;

      if (!dropdownPanel) return;

      setTimeout(() => {
        if (!dropdownPanel || !el || !searchElement || !header || !menu) return;

        let elRect, searchRect, headerRect, menuRect;
        try {
          elRect = el.getBoundingClientRect();
          searchRect = searchElement.getBoundingClientRect();
          headerRect = header.getBoundingClientRect();
          menuRect = menu.getBoundingClientRect();
        } catch (error) {
          return;
        }

        const panelPostionRight = elRect.right - searchRect.right;
        const panelPostionTop = elRect.height;
        const searchElementPositionTop = headerRect.height - menuRect.top;

        try {
          if (this.mobileControllerLegacy.isMobile) {
            dropdownPanel.style.right = `0px`;
            if (dropdownSlotWrapper) {
              dropdownSlotWrapper.style.top = `0px`;
            }
          } else {
            dropdownPanel.style.right = `${panelPostionRight}px`;
            dropdownPanel.style.top = `${panelPostionTop}px`;
            if (dropdownSlotWrapper) {
              dropdownSlotWrapper.style.top = `${searchElementPositionTop - panelPostionTop}px`;
            }
          }
        } catch (error) {
          return;
        }
      }, 200);
    });
  }

  /**
   * If browser is safari, fires setDropdownWidth 300ms after load because safari.
   */
  handleDropdownOnLoad() {
    setTimeout(() => {
      this.setDropdownWidth();
    }, 300);
  }

  setIsMobile() {
    this.isMobile = this.mobileControllerLegacy.isMobile;
  }
}
